import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../LoadingSpinner";

const SessionManager = ({ onMessage, onLoading }) => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [revoking, setRevoking] = useState(new Set());

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setIsLoading(true);
    try {
      // TODO: Implement actual API call
      // const response = await authAPI.getSessions();

      // Mock session data
      const mockSessions = [
        {
          id: "current",
          device: "MacBook Pro",
          browser: "Chrome 118.0",
          os: "macOS 14.0",
          ip: "192.168.1.100",
          location: "San Francisco, CA",
          lastActivity: new Date().toISOString(),
          isCurrent: true,
          userAgent:
            "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36",
        },
        {
          id: "session-2",
          device: "iPhone 14",
          browser: "Safari 17.0",
          os: "iOS 17.0",
          ip: "10.0.1.50",
          location: "San Francisco, CA",
          lastActivity: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          isCurrent: false,
          userAgent: "Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X)",
        },
        {
          id: "session-3",
          device: "Desktop PC",
          browser: "Firefox 119.0",
          os: "Windows 11",
          ip: "203.0.113.45",
          location: "New York, NY",
          lastActivity: new Date(
            Date.now() - 24 * 60 * 60 * 1000,
          ).toISOString(),
          isCurrent: false,
          userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:119.0)",
        },
      ];

      setSessions(mockSessions);
    } catch (error) {
      onMessage({
        type: "error",
        text: "Failed to load active sessions",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const revokeSession = async (sessionId) => {
    if (sessionId === "current") {
      onMessage({
        type: "error",
        text: "Cannot revoke current session",
        details: "Use the logout button to end your current session.",
      });
      return;
    }

    setRevoking((prev) => new Set(prev).add(sessionId));

    try {
      // TODO: Implement actual API call
      // await authAPI.revokeSession(sessionId);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSessions((prev) => prev.filter((session) => session.id !== sessionId));

      onMessage({
        type: "success",
        text: "Session revoked successfully",
      });
    } catch (error) {
      onMessage({
        type: "error",
        text: "Failed to revoke session",
      });
    } finally {
      setRevoking((prev) => {
        const updated = new Set(prev);
        updated.delete(sessionId);
        return updated;
      });
    }
  };

  const revokeAllOtherSessions = async () => {
    const otherSessions = sessions.filter((session) => !session.isCurrent);

    if (otherSessions.length === 0) {
      onMessage({
        type: "warning",
        text: "No other active sessions to revoke",
      });
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to sign out ${otherSessions.length} other session(s)?`,
      )
    ) {
      return;
    }

    onLoading(true);

    try {
      // TODO: Implement actual API call
      // await authAPI.revokeAllOtherSessions();

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setSessions((prev) => prev.filter((session) => session.isCurrent));

      onMessage({
        type: "success",
        text: `Successfully signed out ${otherSessions.length} other session(s)`,
      });
    } catch (error) {
      onMessage({
        type: "error",
        text: "Failed to revoke other sessions",
      });
    } finally {
      onLoading(false);
    }
  };

  const getDeviceIcon = (device, os) => {
    if (
      device.toLowerCase().includes("iphone") ||
      device.toLowerCase().includes("android")
    ) {
      return "📱";
    }
    if (
      device.toLowerCase().includes("ipad") ||
      device.toLowerCase().includes("tablet")
    ) {
      return "📱";
    }
    if (os.toLowerCase().includes("mac")) {
      return "💻";
    }
    if (os.toLowerCase().includes("windows")) {
      return "🖥️";
    }
    return "💻";
  };

  const getBrowserIcon = (browser) => {
    if (browser.toLowerCase().includes("chrome")) return "🌐";
    if (browser.toLowerCase().includes("firefox")) return "🦊";
    if (browser.toLowerCase().includes("safari")) return "🧭";
    if (browser.toLowerCase().includes("edge")) return "📘";
    return "🌐";
  };

  const formatLastActivity = (timestamp) => {
    const now = new Date();
    const activity = new Date(timestamp);
    const diffInMinutes = Math.floor((now - activity) / (1000 * 60));

    if (diffInMinutes < 1) return "Active now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return activity.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Active Sessions</h3>
          <p className="mt-1 text-sm text-gray-600">
            Manage devices that are currently signed in to your account
          </p>
        </div>
        {sessions.filter((session) => !session.isCurrent).length > 0 && (
          <button
            onClick={revokeAllOtherSessions}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            Sign Out All Other Sessions
          </button>
        )}
      </div>

      {/* Sessions List */}
      <div className="space-y-4">
        {sessions.map((session) => (
          <div
            key={session.id}
            className={`bg-white rounded-lg border p-4 ${
              session.isCurrent
                ? "border-primary-200 bg-primary-50"
                : "border-gray-200"
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div className="flex items-center space-x-1 text-2xl">
                  <span>{getDeviceIcon(session.device, session.os)}</span>
                  <span>{getBrowserIcon(session.browser)}</span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h4 className="text-sm font-medium text-gray-900">
                      {session.device}
                    </h4>
                    {session.isCurrent && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                        Current Session
                      </span>
                    )}
                  </div>

                  <div className="mt-1 space-y-1 text-sm text-gray-600">
                    <div className="flex items-center space-x-4">
                      <span>{session.browser}</span>
                      <span>•</span>
                      <span>{session.os}</span>
                    </div>

                    <div className="flex items-center space-x-4">
                      <span>📍 {session.location}</span>
                      <span>•</span>
                      <span>🌐 {session.ip}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${
                          session.isCurrent ? "bg-green-400" : "bg-gray-400"
                        }`}
                      />
                      <span className="text-xs">
                        {formatLastActivity(session.lastActivity)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {!session.isCurrent && (
                <button
                  onClick={() => revokeSession(session.id)}
                  disabled={revoking.has(session.id)}
                  className="ml-4 text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50 flex items-center space-x-1"
                >
                  {revoking.has(session.id) ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span>Revoking...</span>
                    </>
                  ) : (
                    <>
                      <span>🚫</span>
                      <span>Revoke</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Session Details (Expandable) */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <details className="text-xs text-gray-500">
                <summary className="cursor-pointer hover:text-gray-700">
                  Technical Details
                </summary>
                <div className="mt-2 p-2 bg-gray-50 rounded font-mono text-xs break-all">
                  {session.userAgent}
                </div>
              </details>
            </div>
          </div>
        ))}
      </div>

      {/* Security Info */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h4 className="text-sm font-medium text-blue-900 mb-2">
          Security Recommendations
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Regularly review your active sessions for unknown devices</li>
          <li>• Sign out sessions you don't recognize immediately</li>
          <li>
            • Use "Sign Out All Other Sessions" when using a shared computer
          </li>
          <li>• Sessions automatically expire after 30 days of inactivity</li>
        </ul>
      </div>

      {/* Session Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <span className="text-2xl mr-3">📊</span>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {sessions.length}
              </p>
              <p className="text-sm text-gray-600">Total Sessions</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <span className="text-2xl mr-3">📱</span>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {
                  sessions.filter(
                    (s) =>
                      s.device.toLowerCase().includes("iphone") ||
                      s.device.toLowerCase().includes("android"),
                  ).length
                }
              </p>
              <p className="text-sm text-gray-600">Mobile Devices</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <span className="text-2xl mr-3">🌍</span>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {
                  new Set(sessions.map((s) => s.location.split(",")[1]?.trim()))
                    .size
                }
              </p>
              <p className="text-sm text-gray-600">Locations</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionManager;
